import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { ServiceService } from '../../../service.service';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

interface Employee {
  employee_id: string;
  employee_name: string;
}

interface DepartmentData {
  _id: string;
  department_id: string;
  department_name: string;
  weekdays_work_hours?: string;
  friday_work_hours?: string;
  day_shift_employees?: Employee[];
  night_shift_employees?: Employee[];
  day_shift_weekly_schedule?: ScheduleEntry[];
  night_shift_weekly_schedule?: ScheduleEntry[];
}
interface ScheduleEntry {
  day: number;
  employee_id: string;
  employee_name: string;
  workin_time: string;
  workout_time: string;
  lunch_in_time: string;
  lunch_out_time: string;
  isActive: boolean;
}

@Component({
  selector: 'app-shift-assignment',
  templateUrl: './shift-assignment.component.html',
  styleUrls: ['./shift-assignment.component.css'],
})
export class ShiftAssignmentComponent implements OnInit {
  shiftForm: FormGroup;
  employeeDropdown: Employee[] = [];
  isSaving = false;
  departmentId: string = '';
  departmentName: string = '';
  departmentData: DepartmentData | null = null;
  // 🔹 NEW: arrays to hold saved employees from detail API
  savedDayShiftEmployees: Employee[] = [];
  savedNightShiftEmployees: Employee[] = [];


  // Time selection arrays
  private buildWeek() {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((week, i) => ({
      week, value: i + 1, checked: false,
      workin_time: '', workout_time: '', lunch_in_time: '', lunch_out_time: ''
    }));
  }
  dayShiftDays = this.buildWeek();
  nightShiftDays = this.buildWeek();
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  minutes: number[] = Array.from({ length: 60 }, (_, i) => i);

  // Form controls for employee selection
  dayEmployeesControl = new FormControl<Employee[]>([], { nonNullable: true });
  nightEmployeesControl = new FormControl<Employee[]>([], { nonNullable: true });

  // Time fields configuration
  // shiftTimeFields = [
  //   { label: 'Day shift work in time', controlNameHour: 'day_shift_workin_time', controlNameMinute: 'day_shift_workin_minutes' },
  //   { label: 'Day shift work out time', controlNameHour: 'day_shift_workout_time', controlNameMinute: 'day_shift_workout_minutes' },
  //   { label: 'Night shift work in time', controlNameHour: 'night_shift_workin_time', controlNameMinute: 'night_shift_workin_minutes' },
  //   { label: 'Night shift work out time', controlNameHour: 'night_shift_workout_time', controlNameMinute: 'night_shift_workout_minutes' }
  // ];

  Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    }
  });

  constructor(
    private fb: FormBuilder,
    private apiService: ServiceService,
    private router: Router
  ) {
    this.shiftForm = this.fb.group({
      // Time fields
      // day_shift_workin_time: ['', Validators.required],
      // day_shift_workin_minutes: [''],
      // day_shift_workout_time: ['', Validators.required],
      // day_shift_workout_minutes: [''],
      // night_shift_workin_time: ['', Validators.required],
      // night_shift_workin_minutes: [''],
      // night_shift_workout_time: ['', Validators.required],
      // night_shift_workout_minutes: [''],

      // Weekdays and Friday fields
      weekdays_work_hours: ['', Validators.required],
      weekdays_work_minutes: [''],
      friday_work_hours: ['', Validators.required],
      friday_work_minutes: [''],

      // Employee arrays
      day_shift_employees: this.fb.array([], Validators.required),
      night_shift_employees: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.departmentId = localStorage.getItem('department_id') || '';
    this.departmentName = localStorage.getItem('department_name') || 'Department';

    console.log('Department ID from localStorage:', this.departmentId);
    console.log('Department Name from localStorage:', this.departmentName);

    this.initializeEmployeeControls();

    // only this – department data will be loaded from inside loadEmployees()
    this.loadEmployees();
  }


  private initializeEmployeeControls(): void {
    this.syncEmployeeFormArray(this.dayEmployeesControl, 'day_shift_employees');
    this.syncEmployeeFormArray(this.nightEmployeesControl, 'night_shift_employees');
  }

  private syncEmployeeFormArray(control: FormControl<Employee[]>, formArrayName: string): void {
    control.valueChanges.subscribe(selectedEmployees => {
      const fa = this.shiftForm.get(formArrayName) as FormArray;
      fa.clear();
      selectedEmployees?.forEach(emp => {
        fa.push(this.fb.group({
          employee_id: [emp.employee_id],
          employee_name: [emp.employee_name]
        }));
      });
    });
  }

  loadEmployees(): void {
    const dept = this.departmentName || '';

    this.apiService
      .ViewAllEmployeefilter(`?department=${encodeURIComponent(dept)}`)
      .subscribe({
        next: (res: any) => {
          console.log('Employees for department:', dept, res);

          if (res.sucess === 'true' || res.success) {
            this.employeeDropdown = res.data || [];
          } else {
            this.employeeDropdown = [];
          }

          console.log(`Loaded ${this.employeeDropdown.length} employees for ${dept}`);

          // After employees are loaded, load department data / assignments
          if (!this.departmentData) {
            this.loadDepartmentData();
          } else {
            this.loadEmployeeAssignments();
          }
        },
        error: (err: any) => {
          console.error('Error loading employees:', err);
          this.employeeDropdown = [];
          this.Toast.fire({
            icon: 'error',
            title: 'Failed to load employees',
          });
        },
      });
  }


  loadDepartmentData(): void {
    if (!this.departmentId) {
      console.warn('No department ID found - skipping load');
      return;
    }

    console.log('Loading department data for ID (ViewDepartmentID):', this.departmentId);

    // ✅ use the real method from service.service.ts
    this.apiService.ViewDepartmentID(this.departmentId).subscribe({
      next: (response: any) => {
        console.log('Department API response:', response);

        if (response.success && response.data && response.data.length > 0) {
          // Find the specific department by department_id
          const department = response.data.find((dept: any) =>
            dept.department_id === this.departmentId
          );

          if (department) {
            this.departmentData = department;
            console.log('Found department data:', this.departmentData);

            // patch hours/minutes into the form
            this.patchTimeValues(department);

            // load employee selections into the ng-selects
            if (this.employeeDropdown.length > 0) {
              this.loadEmployeeAssignments();
            }
          } else {
            console.log('Department not found in response');
            this.Toast.fire({
              icon: 'warning',
              title: 'Department data not found',
            });
          }
        } else {
          console.log('No department data found in response');
        }
      },
      error: (err: any) => {
        console.error('Error loading department data:', err);
        this.Toast.fire({
          icon: 'error',
          title: 'Failed to load department data',
        });
      },
    });
  }

  removeDayEmployee(emp: Employee): void {
    const current = this.dayEmployeesControl.value || [];
    const updated = current.filter(e => e.employee_id !== emp.employee_id);
    this.dayEmployeesControl.setValue(updated);
  }

  removeNightEmployee(emp: Employee): void {
    const current = this.nightEmployeesControl.value || [];
    const updated = current.filter(e => e.employee_id !== emp.employee_id);
    this.nightEmployeesControl.setValue(updated);
  }

  clearDayEmployees(): void {
    this.dayEmployeesControl.setValue([]);
  }

  clearNightEmployees(): void {
    this.nightEmployeesControl.setValue([]);
  }


  private loadEmployeeAssignments(): void {
    if (!this.departmentData) {
      console.log('Cannot load employee assignments - missing department data');
      return;
    }

    console.log('Loading employee assignments...');

    // Get employees directly from department data (they already have full employee objects)
    const daySelected: Employee[] = (this.departmentData.day_shift_employees || []).map(e => ({ employee_id: e.employee_id, employee_name: e.employee_name }));
    const nightSelected: Employee[] = (this.departmentData.night_shift_employees || []).map(e => ({ employee_id: e.employee_id, employee_name: e.employee_name }));

    console.log('Day shift employees from API:', daySelected);
    console.log('Night shift employees from API:', nightSelected);

    // Set employee controls WITHOUT emitting events
    this.dayEmployeesControl.setValue(daySelected, { emitEvent: false });
    this.nightEmployeesControl.setValue(nightSelected, { emitEvent: false });

    // Update FormArrays to match selected employees
    this.updateEmployeeFormArray('day_shift_employees', daySelected);
    this.updateEmployeeFormArray('night_shift_employees', nightSelected);

    console.log('✅ Successfully loaded employee assignments');
    console.log('Day shift employees count:', daySelected.length);
    console.log('Night shift employees count:', nightSelected.length);
  }

  private patchTimeValues(departmentData: DepartmentData): void {
    console.log('Patching time values from department data:', departmentData);

    // Helper function to safely parse time strings
    const parseTime = (timeString: string | undefined) => {
      if (!timeString) return { hours: '', minutes: '' };

      try {
        const [hours, minutes] = timeString.split(':');
        return {
          hours: hours ? parseInt(hours, 10) : '',
          minutes: minutes ? parseInt(minutes, 10) : ''
        };
      } catch (error) {
        console.error('Error parsing time:', timeString, error);
        return { hours: '', minutes: '' };
      }
    };

    // Patch weekdays work hours
    if (departmentData.weekdays_work_hours) {
      const weekdaysTime = parseTime(departmentData.weekdays_work_hours);
      this.shiftForm.patchValue({
        weekdays_work_hours: weekdaysTime.hours,
        weekdays_work_minutes: weekdaysTime.minutes
      });
    }

    // Patch friday work hours
    if (departmentData.friday_work_hours) {
      const fridayTime = parseTime(departmentData.friday_work_hours);
      this.shiftForm.patchValue({
        friday_work_hours: fridayTime.hours,
        friday_work_minutes: fridayTime.minutes
      });
    }

    this.applySchedule(departmentData.day_shift_weekly_schedule, this.dayShiftDays);
    this.applySchedule(departmentData.night_shift_weekly_schedule, this.nightShiftDays);

    console.log('✅ Time values patched successfully');
    console.log('Form values after patching:', this.shiftForm.value);
  }

  private updateEmployeeFormArray(formArrayName: string, employees: Employee[]): void {
    const fa = this.shiftForm.get(formArrayName) as FormArray;
    fa.clear();
    employees.forEach(emp => {
      fa.push(this.fb.group({
        employee_id: [emp.employee_id],
        employee_name: [emp.employee_name]
      }));
    });
  }

  // Helper function to format time as "HH:MM"
  formatTime(hours: number, minutes: number): string {
    const formattedHours = String(hours || 0).padStart(2, '0');
    const formattedMinutes = String(minutes || 0).padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
  }

  onSubmit(): void {
    if (this.shiftForm.invalid) {
      this.shiftForm.markAllAsTouched();
      this.Toast.fire({
        icon: "error",
        title: "Please fill all required fields"
      });
      return;
    }

    if (!this.departmentId) {
      this.Toast.fire({
        icon: "error",
        title: "Department ID not found"
      });
      return;
    }

    const weekdayValidation = this.validateWeekdayTimes();
    if (!weekdayValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Time Fields',
        text: weekdayValidation.message,
        confirmButtonText: 'OK'
      });
      return;
    }

    const invalidNight = this.nightShiftDays.find(d => this.isNightTimeInvalid(d));
    if (invalidNight) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Night Shift Time',
        text: `Night shift: Work in time must be greater than Work out time for ${invalidNight.week}.`,
        confirmButtonText: 'OK'
      });
      return;
    }

    const payload = {
      department_id: this.departmentId,
      weekdays_work_hours: this.formatTime(
        this.shiftForm.value.weekdays_work_hours,
        this.shiftForm.value.weekdays_work_minutes
      ),
      friday_work_hours: this.formatTime(
        this.shiftForm.value.friday_work_hours,
        this.shiftForm.value.friday_work_minutes
      ),
      day_shift_employees: this.dayEmployeesControl.value.map(e => ({ employee_id: e.employee_id, employee_name: e.employee_name })),
      day_shift_weekly_schedule: this.buildShiftSchedule(this.dayShiftDays),
      night_shift_employees: this.nightEmployeesControl.value.map(e => ({ employee_id: e.employee_id, employee_name: e.employee_name })),
      night_shift_weekly_schedule: this.buildShiftSchedule(this.nightShiftDays),
    };

    console.log('🚀 Shift assignment payload:', JSON.stringify(payload, null, 2));

    this.isSaving = true;

    // Use EditDepartment which calls updateDepartments endpoint
    this.apiService.EditDepartment(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        console.log('✅ Update response:', res);

        if (res && res.success) {
          this.Toast.fire({
            icon: "success",
            title: res.message || "Shift assignment updated successfully"
          });

          // Navigate to listing page
          setTimeout(() => {
            this.router.navigate(['/admin/department-list']);
          }, 1500);
        } else {
          console.error('❌ Update returned failure:', res);
          this.Toast.fire({
            icon: "error",
            title: res?.message || "Failed to update shift assignment"
          });
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        console.error('💥 Error in update:', err);
        this.Toast.fire({
          icon: "error",
          title: err.error?.message || "Failed to update shift assignment"
        });
      },
    });
  }
  weekSelected(day: any): void {
    day.checked = !day.checked;
  }
  isNightTimeInvalid(day: any): boolean {
  return !!day.checked && !!day.workin_time && !!day.workout_time
    && day.workin_time <= day.workout_time;
}

  // NEW
  private validateWeekdayTimes(): { isValid: boolean; message: string } {
    const groups = [
      { name: 'Day shift', days: this.dayShiftDays, emps: this.dayEmployeesControl.value },
      { name: 'Night shift', days: this.nightShiftDays, emps: this.nightEmployeesControl.value }
    ];
    for (const g of groups) {
      const checked = g.days.filter(d => d.checked);
      if (checked.length && !g.emps.length) {
        return { isValid: false, message: `${g.name}: select employees before choosing working days.` };
      }
      for (const day of checked) {
        if (!day.workin_time || !day.workout_time || !day.lunch_in_time || !day.lunch_out_time) {
          return {
            isValid: false,
            message: `${g.name}: fill all time fields (Work in, Work out, Lunch in, Lunch out) for ${day.week}.`
          };
        }
      }
    }
    return { isValid: true, message: '' };
  }

  fillAllTimes(days: any[]): void {
    const sourceDay = days.find(
      d => d.checked && (d.workin_time || d.workout_time || d.lunch_in_time || d.lunch_out_time)
    );

    if (!sourceDay) {
      Swal.fire({
        icon: 'warning',
        title: 'Nothing to copy',
        text: 'Fill the time fields for at least one selected day first.'
      });
      return;
    }

    days.forEach(day => {
      if (day.checked) {
        day.workin_time = sourceDay.workin_time;
        day.workout_time = sourceDay.workout_time;
        day.lunch_in_time = sourceDay.lunch_in_time;
        day.lunch_out_time = sourceDay.lunch_out_time;
      }
    });

    this.Toast.fire({
      icon: 'success',
      title: 'Times applied to all selected days'
    });
  }

  resetForm(): void {
    this.shiftForm.reset({
      weekdays_work_hours: '',
      weekdays_work_minutes: '',
      friday_work_hours: '',
      friday_work_minutes: '',
    });
    this.dayEmployeesControl.setValue([]);
    this.nightEmployeesControl.setValue([]);

    const dayArray = this.shiftForm.get('day_shift_employees') as FormArray;
    const nightArray = this.shiftForm.get('night_shift_employees') as FormArray;
    dayArray.clear();
    nightArray.clear();
    this.applySchedule([], this.dayShiftDays);
    this.applySchedule([], this.nightShiftDays);
  }

  employeeSelect(): void {
    console.log("Selected Day Shift Employees:", this.dayEmployeesControl.value);
    console.log("Selected Night Shift Employees:", this.nightEmployeesControl.value);
  }

  // Compare function for ng-select to match employees by employee_id
  compareEmployees(emp1: Employee, emp2: Employee): boolean {
    return emp1 && emp2 ? emp1.employee_id === emp2.employee_id : emp1 === emp2;
  }
  private uniqueEmployees(list?: ScheduleEntry[]): Employee[] {
    const map = new Map<string, Employee>();
    (list || []).forEach(s => map.set(s.employee_id, { employee_id: s.employee_id, employee_name: s.employee_name }));
    return Array.from(map.values());
  }

  // API array -> week grid (times taken from the first entry of each day)
  private applySchedule(list: ScheduleEntry[] | undefined, days: any[]): void {
    days.forEach(d => {
      d.checked = false; d.workin_time = ''; d.workout_time = ''; d.lunch_in_time = ''; d.lunch_out_time = '';
    });
    const filled = new Set<number>();
    (list || []).forEach(s => {
      const d = days.find(x => x.value === s.day);
      if (!d) return;
      if (s.isActive) d.checked = true;
      if (!filled.has(s.day)) {
        filled.add(s.day);
        d.workin_time = this.to24h(s.workin_time);
        d.workout_time = this.to24h(s.workout_time);
        d.lunch_in_time = this.to24h(s.lunch_in_time);
        d.lunch_out_time = this.to24h(s.lunch_out_time);
      }
    });
  }

  // week grid -> API array (one entry per checked day per employee)
  private buildShiftSchedule(days: any[]): any[] {
    return days.filter(d => d.checked).map(d => ({
      day: d.value,
      workin_time: d.workin_time,
      workout_time: d.workout_time,
      lunch_in_time: d.lunch_in_time,
      lunch_out_time: d.lunch_out_time,
      isActive: true
    }));
  }

  // "13:05" -> "01:05 PM"
  private to12h(t: string): string {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ap = h >= 12 ? 'PM' : 'AM';
    return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ap}`;
  }

  // "01:05 PM" -> "13:05"
  private to24h(t: string): string {
    if (!t) return '';
    const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return t;
    let h = parseInt(m[1], 10) % 12;
    if (m[3].toUpperCase() === 'PM') h += 12;
    return `${String(h).padStart(2, '0')}:${m[2]}`;
  }
}